import { useCallback } from 'react';
import { useQueryClient } from '@tanstack/react-query';

import { api, FctReport } from '@/api';
import { GetRadiologistNoteResponse } from '@/api/model';
import { useAddendum } from '@/core/addendum';
import { useStudyGuard } from '@/shared/guard';
import { useFctReport } from '@/shared/hook';
import { formatEditorString, isEmpty, isNotEmpty } from '@/shared/util';

import { FinalImpressionFieldErrors } from '../model';
import { isConsultNoteRequired } from '../util/consult-note.util';
import { usePreviousStudies } from './use-previous-studies.hook';

export function useValidateFinalImpression() {
  const queryClient = useQueryClient();
  const { hasPreviousStudyResults, isError: isPriorStudiesError } = usePreviousStudies();
  const { report: currentReport } = useFctReport();
  const { isPoetryStudy } = useStudyGuard();

  const { isAddendumMode } = useAddendum();

  const shouldLoadConsultNote =
    isPoetryStudy && !!currentReport?.carePlanId && isNotEmpty(currentReport?.uuid);

  const { data: consultNoteQueryData } = api.fct.radiologistNote.query.useGetRadiologistNote(
    {
      payload: {
        reportUuid: currentReport?.uuid ?? '',
      },
    },
    {
      enabled: shouldLoadConsultNote,
    },
  );

  return useCallback(
    (report: FctReport, addendumText?: string): FinalImpressionFieldErrors => {
      const isFinalImpressionsRequired = report?.config?.isFinalImpressionsRequired !== false;

      const isUrgencyCategoryRequired = report?.config?.isUrgencyCategoryRequired !== false;

      const missingFinalImpressions =
        isFinalImpressionsRequired && isEmpty(formatEditorString(report?.finalImpressions ?? ''));

      const missingUrgencyCategory = isUrgencyCategoryRequired && isEmpty(report?.urgencyCategory);

      // priorStudyIds validation:
      // - null: user selected "No relevant comparison available" (valid)
      // - undefined: no selection made (invalid)
      // - []: empty array means no scans selected (invalid)
      // - ['id']: scans selected (valid)
      // When the prior studies API fails, treat as missing to prevent fail-open
      const shouldRequireComparison = hasPreviousStudyResults || isPriorStudiesError;

      const missingComparisonData =
        shouldRequireComparison &&
        (report?.priorStudyIds === undefined ||
          (Array.isArray(report?.priorStudyIds) && isEmpty(report.priorStudyIds)));

      const missingAddendumText = isAddendumMode && isEmpty(formatEditorString(addendumText ?? ''));

      const consultNote =
        queryClient.getQueryData<GetRadiologistNoteResponse>([
          api.fct.radiologistNote.KEY.GET_RADIOLOGIST_NOTE,
          report?.uuid ?? '',
        ]) ?? consultNoteQueryData;

      const shouldRequireConsultNote =
        isPoetryStudy &&
        !!report?.carePlanId &&
        isConsultNoteRequired({
          publishedNotes: consultNote?.published,
          urgencyCategory: report?.urgencyCategory,
        });

      const missingConsultNote =
        shouldRequireConsultNote && isEmpty(consultNote?.draft?.content ?? '');

      return {
        missingAddendumText,
        missingComparisonData,
        missingConsultNote,
        missingFinalImpressions,
        missingUrgencyCategory,
      };
    },
    [
      consultNoteQueryData,
      hasPreviousStudyResults,
      isPriorStudiesError,
      isAddendumMode,
      isPoetryStudy,
      queryClient,
    ],
  );
}
